import MessageApi from 'dashboard/api/inbox/message';
import TasksAPI from 'dashboard/api/tasks';

/**
 * Obtiene todas las tareas de forma optimizada usando el endpoint dedicado
 * @returns {Promise<Array>} - Array de tareas
 */
export const getAllTasksOptimized = async () => {
  try {
    const response = await TasksAPI.getTasks();
    if (response && response.data) {
      return response.data;
    }
    return [];
  } catch (error) {
    // Silently fail and return empty array
    return [];
  }
};

/**
 * Parsea un mensaje de tarea desde el formato de texto
 * @param {Object} message - Mensaje de Chatwoot
 * @returns {Object|null} - Objeto de tarea parseado o null si no es una tarea
 */
export const parseTaskFromMessage = message => {
  if (!message.content || !message.content.startsWith('[TAREA]')) {
    return null;
  }

  const lines = message.content.split('\n');
  const title = lines[0].replace('[TAREA]', '').trim();

  // Extraer información usando regex
  const descriptionMatch = message.content.match(/📋 Descripción: (.+)/);
  const dueDateMatch = message.content.match(/📅 Vence: (.+)/);
  const assignedMatch = message.content.match(/👤 Asignado: @(.+)/);
  const statusMatch = message.content.match(
    /⏸️ Estado: (.+)|✅ Estado: (.+)|🔄 Estado: (.+)/
  );

  const status =
    statusMatch?.[1] || statusMatch?.[2] || statusMatch?.[3] || 'Pendiente';
  const isCompleted = status.includes('Completada') || status.includes('✅');

  return {
    id: message.id,
    conversationId: message.conversation_id,
    title,
    description: descriptionMatch ? descriptionMatch[1] : '',
    dueDate: dueDateMatch ? dueDateMatch[1] : '',
    assignedTo: assignedMatch ? assignedMatch[1] : '',
    status,
    isCompleted,
    createdAt: message.created_at,
    sender: message.sender,
    rawMessage: message,
  };
};

/**
 * Obtiene todas las tareas de todas las conversaciones
 * @param {Array} conversations - Array de conversaciones
 * @returns {Promise<Array>} - Array de tareas
 */
export const getAllTasks = async conversations => {
  const taskPromises = conversations.map(async conversation => {
    try {
      const response = await MessageApi.getPreviousMessages({
        conversationId: conversation.id,
      });

      if (response && response.data && response.data.payload) {
        return response.data.payload
          .filter(msg => msg.private && msg.content?.startsWith('[TAREA]'))
          .map(msg => {
            const task = parseTaskFromMessage(msg);
            if (task) {
              return {
                ...task,
                conversation,
              };
            }
            return null;
          })
          .filter(Boolean);
      }
      return [];
    } catch (error) {
      // Silently fail for individual conversations
      return [];
    }
  });

  const results = await Promise.all(taskPromises);
  return results.flat();
};

/**
 * Marca una tarea como completada o pendiente
 * @param {Object} task - Tarea a actualizar
 * @param {Boolean} completed - Estado de completado
 * @returns {Promise} - Promesa de la actualización
 */
export const toggleTaskStatus = async (task, completed) => {
  const updatedContent = task.rawMessage.content.replace(
    /⏸️ Estado: .+|✅ Estado: .+|🔄 Estado: .+/,
    `${completed ? '✅' : '⏸️'} Estado: ${
      completed ? 'Completada' : 'Pendiente'
    }`
  );

  // Eliminar el mensaje anterior para evitar duplicados
  await MessageApi.delete(task.conversationId, task.id);

  // Crear un nuevo mensaje privado con el estado actualizado
  return MessageApi.create({
    conversationId: task.conversationId,
    message: updatedContent,
    private: true,
  });
};

/**
 * Actualiza el estado de una tarea
 * @param {Object} task - Tarea a actualizar
 * @param {String} newStatus - Nuevo estado ('Pendiente', 'En Proceso', 'Completada')
 * @returns {Promise} - Promesa de la actualización
 */
export const updateTaskStatus = async (task, newStatus) => {
  let emoji = '⏸️';
  if (newStatus === 'En Proceso') emoji = '🔄';
  if (newStatus === 'Completada') emoji = '✅';

  const updatedContent = task.rawMessage.content.replace(
    /⏸️ Estado: .+|✅ Estado: .+|🔄 Estado: .+/,
    `${emoji} Estado: ${newStatus}`
  );

  // Eliminar el mensaje anterior para evitar duplicados
  await MessageApi.delete(task.conversationId, task.id);

  // Crear un nuevo mensaje privado con el estado actualizado
  return MessageApi.create({
    conversationId: task.conversationId,
    message: updatedContent,
    private: true,
  });
};

/**
 * Filtra tareas por usuario asignado
 * @param {Array} tasks - Array de tareas
 * @param {String} userName - Nombre del usuario
 * @returns {Array} - Tareas filtradas
 */
export const filterTasksByUser = (tasks, userName) => {
  if (!userName) return tasks;
  return tasks.filter(task => {
    // Soportar tanto assignedTo (camelCase) como assigned_to (snake_case)
    const assignedTo = task.assignedTo || task.assigned_to || '';
    return assignedTo.toLowerCase().includes(userName.toLowerCase());
  });
};

/**
 * Filtra tareas por estado
 * @param {Array} tasks - Array de tareas
 * @param {String} status - 'pending', 'completed', o 'all'
 * @returns {Array} - Tareas filtradas
 */
export const filterTasksByStatus = (tasks, status) => {
  if (status === 'all') return tasks;
  if (status === 'completed') {
    return tasks.filter(task => {
      const isCompleted = task.isCompleted ?? task.is_completed ?? false;
      return isCompleted;
    });
  }
  if (status === 'pending') {
    return tasks.filter(task => {
      const isCompleted = task.isCompleted ?? task.is_completed ?? false;
      return !isCompleted;
    });
  }
  return tasks;
};

/**
 * Ordena tareas por fecha de vencimiento
 * @param {Array} tasks - Array de tareas
 * @param {String} order - 'asc' o 'desc'
 * @returns {Array} - Tareas ordenadas
 */
export const sortTasksByDueDate = (tasks, order = 'asc') => {
  return [...tasks].sort((a, b) => {
    const dateA = new Date(a.dueDate);
    const dateB = new Date(b.dueDate);
    return order === 'asc' ? dateA - dateB : dateB - dateA;
  });
};
